
/**
 * BcmSettings Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class BcmSettingsService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'BcmSettings listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bcm-settings list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bcm-settings');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'BcmSettings oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create bcm-settings');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'BcmSettings güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update bcm-settings');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'BcmSettings silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete bcm-settings');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
